<script>
  import { format } from 'svelte-i18n';
  import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from '@sveltestrap/sveltestrap';

  import { Field, FieldActions, FieldHeader, FieldLabel, FieldValue } from '@client/components/fields/standalone/base';
  import { Icon } from '@client/components/icon';
  import { Input } from '@client/components/input';
  import { Spinner } from '@client/components/spinner';

  import { DomainsService } from '@client/services/domains';

  import { userStore } from '@client/stores/user';

  export let id = null;
  export let label = null;
  export let addButtonLabel = $format('label.ADD');
  export let allowCopy = false;
  export let allowBulkAdd = false;
  export let allowBillingIdLookup = false;
  export let bulkAddLabel = $format('label.BULK_ADD');
  export let bulkAddPlaceholder = $format('label.BULK_ADD_PLACEHOLDER');

  export let values = [];
  export let formatValue = (val) => val?.join(', ') || '-';

  export let validation = undefined;
  export let validationError = undefined;

  export let tooltip = undefined;
  export let tooltipIcon = undefined;

  export let readonly = false;
  export let editing = false;

  export let showActions = true;

  export let onUpdate = () => {};

  let disableSubmit = true;
  let items = [...values];

  let refs = [];
  let errors = [];
  let bulkInput = '';
  let bulkError = '';
  let loadingDomains = false;
  let showBulkInput = false;

  const onEdit = () => {
    editing = true;

    setTimeout(() => refs[0] && refs[0].focus(), 100);
  };

  const onCancel = () => {
    editing = false;
    items = [...values];
    errors = [];
    bulkInput = '';
    bulkError = '';
    showBulkInput = false;
    loadingDomains = false;
  };

  const onSave = () => {
    let error = false;

    if (items.length && validation) {
      items.forEach((value, index) => {
        if (value?.length && !validation(value)) {
          errors[index] = validationError || $format('label.ERROR_INVALID_VALUES');
          error = true;
        }
      });

      if (error) {
        return;
      }
    }

    const seen = new Set();
    items.forEach((value, index) => {
      if (!value) return;
      const lower = value.toLowerCase();
      if (seen.has(lower)) {
        errors[index] = $format('label.ERROR_DUPLICATE_VALUE');
        error = true;
      }
      seen.add(lower);
    });

    if (error) {
      return;
    }

    values = [...items.filter(Boolean)];
    items = [...values];

    editing = false;

    if (onUpdate) {
      onUpdate(id, label, values);
    }
  };

  const onAdd = () => {
    if (allowBulkAdd && bulkInput.trim()) {
      bulkError = '';

      const newValues = bulkInput
        .split(',')
        .map((v) => v.trim())
        .filter((v) => v.length > 0);

      if (newValues.length === 0) {
        bulkError = $format('label.ERROR_NO_VALID_VALUES');
        return;
      }

      if (validation) {
        const invalidValues = newValues.filter((v) => !validation(v));
        if (invalidValues.length > 0) {
          bulkError = `${validationError || $format('label.ERROR_INVALID_VALUES')}: ${invalidValues.join(', ')}`;
          return;
        }
      }

      const seen = new Set(items.filter(Boolean).map((v) => v.toLowerCase()));
      const duplicates = [];
      const unique = [];

      for (const v of newValues) {
        const lower = v.toLowerCase();
        seen.has(lower) ? duplicates.push(v) : unique.push(v);
        seen.add(lower);
      }

      if (duplicates.length > 0) {
        const key = duplicates.length === 1 ? 'label.ERROR_DUPLICATE_VALUE' : 'label.ERROR_DUPLICATE_VALUES';
        bulkError = `${$format(key)}: ${duplicates.join(', ')}`;
        return;
      }

      items = [...items, ...unique];
      errors = [...errors, ...unique.map(() => '')];

      bulkInput = '';
    } else {
      items = [...items, ''];
      errors = [...errors, ''];
      setTimeout(() => refs.length && refs.slice(-1)[0]?.focus(), 100);
    }
  };

  const onRemove = (index) => {
    errors.splice(index, 1);
    errors = [...errors];

    items.splice(index, 1);
    items = [...items];

    const minIndex = Math.max(index - 1, 0);

    setTimeout(() => refs.length && refs[minIndex]?.focus(), 100);
  };

  const showBulkAddInput = () => {
    showBulkInput = true;
  };

  const findDomainsByBillingId = async () => {
    showBulkInput = true;
    loadingDomains = true;
    bulkError = '';

    try {
      const currentDomain = $userStore?.domain;
      if (!currentDomain) {
        bulkError = $format('label.ERROR_UNABLE_TO_DETERMINE_DOMAIN');
        loadingDomains = false;
        return;
      }

      const currentDomainResponse = await DomainsService.getAll({
        query: currentDomain,
        page: 1,
        perPage: 1,
        screen: 'staff'
      });

      const domain = currentDomainResponse?.data?.[0];
      const domainSettings = domain?.domainsettings;
      const billingId = domainSettings?.billing_id || domainSettings?.billing_customer_id;

      if (!billingId) {
        bulkError = $format('label.ERROR_NO_BILLING_ID');
        loadingDomains = false;
        return;
      }

      const { data } = await DomainsService.getAll({
        billingid: billingId,
        debounceBillingid: '',
        debounceQuery: '',
        page: 1,
        perPage: 1000,
        query: '',
        screen: 'staff'
      });

      if (data && data.length > 0) {
        const domainNames = data.map(({ domainname }) => domainname).join(', ');
        bulkInput = domainNames;
        bulkError = '';
      } else {
        bulkError = $format('label.ERROR_NO_DOMAINS_FOUND');
      }
    } catch (error) {
      bulkError = $format('label.ERROR_FAILED_TO_FETCH_DOMAINS');
      console.error('Error fetching domains by billing ID:', error);
    }

    loadingDomains = false;
  };

  $: effectiveAddButtonLabel = bulkInput.trim() ? bulkAddLabel : addButtonLabel;

  $: disableSubmit = values?.join() === items.filter(Boolean).join();
  $: hasErrors = errors.filter(Boolean).length;
  $: copyValue = formatValue(values);
</script>

<Field>
  <FieldHeader>
    <FieldLabel {label} {tooltipIcon} {tooltip} bind:error={hasErrors} />
    <FieldActions
      bind:readonly
      bind:editing
      bind:disableSubmit
      {onSave}
      {onEdit}
      {onCancel}
      {showActions}
      {allowCopy}
      bind:copyValue
    />
  </FieldHeader>
  <FieldValue {showActions}>
    <div class="field-list">
      {#if editing}
        <slot name="edit" />

        {#if allowBulkAdd}
          {#if showBulkInput}
            <div class="bulk-input-wrapper">
              <textarea
                class="bulk-input"
                bind:value={bulkInput}
                placeholder={bulkAddPlaceholder}
                rows="3"
                disabled={loadingDomains}
              />
              {#if loadingDomains}
                <div class="bulk-input-spinner">
                  <Spinner show inline />
                </div>
              {/if}
            </div>
            {#if bulkError}
              <div class="bulk-error">{bulkError}</div>
            {/if}
          {/if}
        {/if}

        <ul>
          {#each items as value, index}
            <li>
              <div class="item">
                <Input
                  type="text"
                  class={`raw ${errors[index] ? 'error' : ''}`}
                  bind:ref={refs[index]}
                  bind:value
                  on:keyup={() => (errors[index] = '')}
                />
                <Icon name="x-circle" on:click={() => onRemove(index)} role="button" tabindex="0" />
              </div>
              {#if errors[index]}
                <div class="error">{errors[index]}</div>
              {/if}
            </li>
          {/each}
        </ul>
        <div class="actions-wrapper">
          <div>
            <Button color="primary" on:click={onAdd}>
              {@html effectiveAddButtonLabel}
            </Button>
          </div>
          {#if allowBulkAdd}
            <Dropdown class="bulk-actions-dropdown" direction="up" autoClose>
              <DropdownToggle color="secondary">
                {$format('label.ACTIONS')}
                <Icon name="chevron-up" />
              </DropdownToggle>
              <DropdownMenu end>
                <DropdownItem on:click={showBulkAddInput}>
                  {bulkAddLabel}
                </DropdownItem>
                {#if allowBillingIdLookup}
                  <DropdownItem on:click={findDomainsByBillingId} disabled={loadingDomains}>
                    {$format('label.GET_DOMAINS_BY_BILLING_ID')}
                  </DropdownItem>
                {/if}
              </DropdownMenu>
            </Dropdown>
          {/if}
        </div>
      {:else}
        {formatValue(values)}
      {/if}
    </div>
  </FieldValue>
  {#if !editing}
    <slot name="preview" />
  {/if}
</Field>

<style lang="scss" src="./list.scss"></style>
